import { Link } from 'react-router-dom';
import { Dumbbell, Users, Clock, Award, ChevronRight, MapPin, Phone, Mail, Heart, Music, Droplets, Target, Star, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_gym-roster-pro-1/artifacts/2uba3mjm_goat.jpg";
// Jatin Photo URL
const JATIN_PHOTO_URL = "https://customer-assets.emergentagent.com/job_gym-roster-pro-1/artifacts/vqdje5j6_jatin.webp";

const LandingPage = () => {
  const facilities = [
    {
      icon: Dumbbell,
      title: 'Premium Equipment',
      description: 'State-of-the-art machines and free weights from top international brands for complete workout.'
    },
    {
      icon: Music,
      title: 'Zumba Classes',
      description: 'High-energy Zumba sessions to make your cardio fun and effective. Dance your way to fitness!'
    },
    {
      icon: Heart,
      title: 'Dance Classes',
      description: 'Learn various dance forms while burning calories. Perfect blend of fun and fitness.'
    },
    {
      icon: Droplets,
      title: 'Steam Room',
      description: 'Relax and recover in our premium steam room. Perfect for post-workout muscle relaxation.'
    },
    {
      icon: Target,
      title: 'Personal Training',
      description: 'One-on-one guidance from IIFEM certified trainers for personalized fitness goals.'
    },
    {
      icon: Clock,
      title: 'Flexible Timings',
      description: 'Open from 6 AM to 10 PM. Train when it suits your schedule best.'
    }
  ];

  const whyChooseUs = [
    'IIFEM Certified Professional Trainers',
    'Air Conditioned Workout Area',
    'Separate Sections for Men & Women',
    'Modern Cardio Equipment',
    'Strength Training Zone',
    'Clean & Hygienic Environment',
    'Free Fitness Assessment',
    'Diet & Nutrition Guidance'
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#050505]/95 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <img 
                src={LOGO_URL} 
                alt="G.O.A.T Fitness - Best Gym in Pathankot" 
                className="h-14 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#facilities" className="text-zinc-400 hover:text-white transition-colors text-sm uppercase tracking-wider">Facilities</a>
              <a href="#about" className="text-zinc-400 hover:text-white transition-colors text-sm uppercase tracking-wider">About</a>
              <a href="#why-us" className="text-zinc-400 hover:text-white transition-colors text-sm uppercase tracking-wider">Why Us</a>
              <a href="#contact" className="text-zinc-400 hover:text-white transition-colors text-sm uppercase tracking-wider">Contact</a>
            </div>
            <a href="tel:+919888156428">
              <Button 
                data-testid="call-now-btn"
                className="bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider rounded-none"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0 hero-gradient" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(100%)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <p className="font-['Inter'] uppercase text-xs tracking-[0.2em] text-[#FF3B30] mb-4 animate-fadeInUp">
              Greatest Of All Time
            </p>
            <h1 className="font-['Barlow_Condensed'] font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-none mb-4 animate-fadeInUp animation-delay-100">
              Best Gym in<br />
              <span className="text-[#FF3B30] text-glow-red">Pathankot</span>
            </h1>
            <p className="font-['Inter'] text-zinc-400 text-lg mb-8 max-w-xl animate-fadeInUp animation-delay-200">
              Transform your body at Pathankot's premier fitness destination. 
              Zumba classes, Dance sessions, Steam room, and personalized training 
              by IIFEM certified professionals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp animation-delay-300">
              <a href="tel:+919888156428">
                <Button 
                  data-testid="hero-call-btn"
                  className="bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-none shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)]"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  098881 56428
                </Button>
              </a>
              <a href="#contact">
                <Button 
                  data-testid="visit-btn"
                  variant="outline"
                  className="border-white/20 text-white hover:border-[#FF3B30] hover:text-[#FF3B30] font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-none"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Visit Us
                </Button>
              </a>
            </div>
            
            {/* Location Badge */}
            <div className="mt-8 animate-fadeInUp animation-delay-400">
              <div className="inline-flex items-center gap-2 bg-zinc-900/80 border border-zinc-800 px-4 py-2">
                <MapPin className="w-4 h-4 text-[#FF3B30]" />
                <span className="text-zinc-300 text-sm">Above HDFC Bank, Saili Road, Pathankot</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section id="facilities" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-['Inter'] uppercase text-xs tracking-[0.2em] text-[#FF3B30] mb-4">
              Our Facilities
            </p>
            <h2 className="font-['Barlow_Condensed'] font-extrabold text-4xl sm:text-5xl uppercase tracking-tighter text-white">
              World-Class Amenities
            </h2>
            <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
              Everything you need for a complete fitness experience under one roof
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {facilities.map((facility, index) => (
              <article 
                key={index}
                className="feature-card group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-[#FF3B30]/10 flex items-center justify-center mb-6 group-hover:bg-[#FF3B30]/20 transition-colors">
                  <facility.icon className="w-7 h-7 text-[#FF3B30]" />
                </div>
                <h3 className="font-['Barlow_Condensed'] font-bold text-xl uppercase tracking-tight text-white mb-3">
                  {facility.title}
                </h3>
                <p className="font-['Inter'] text-zinc-500 text-sm leading-relaxed">
                  {facility.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* About / Trainer Section */}
      <section id="about" className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Trainer Photo */}
            <div className="relative">
              <div className="aspect-[4/5] bg-zinc-900 border border-zinc-800 overflow-hidden">
                <img 
                  src={JATIN_PHOTO_URL}
                  alt="Mr. Jatin Sharma - IIFEM Certified Trainer at G.O.A.T Fitness Pathankot"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                
                {/* Trainer Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="bg-[#FF3B30] inline-block px-3 py-1 mb-3">
                    <span className="text-white text-xs font-bold uppercase tracking-wider">IIFEM Certified</span>
                  </div>
                  <h3 className="font-['Barlow_Condensed'] font-extrabold text-3xl text-white uppercase">
                    Mr. Jatin Sharma
                  </h3>
                  <p className="text-zinc-300">Head Trainer & Founder</p>
                </div>
              </div>
              
              {/* Decorative Element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#FF3B30]/30 -z-10" />
            </div>

            {/* About Content */}
            <div>
              <p className="font-['Inter'] uppercase text-xs tracking-[0.2em] text-[#FF3B30] mb-4">
                Meet Your Trainer
              </p>
              <h2 className="font-['Barlow_Condensed'] font-extrabold text-4xl sm:text-5xl uppercase tracking-tighter text-white mb-6">
                The G.O.A.T Trainer
              </h2>
              <p className="text-zinc-400 text-lg mb-6">
                Mr. Jatin Sharma, an <strong className="text-white">IIFEM Certified fitness professional</strong>, 
                brings years of expertise to help you achieve your fitness goals. His personalized approach 
                and dedication have transformed hundreds of lives in Pathankot.
              </p>
              <p className="text-zinc-400 mb-8">
                At G.O.A.T Fitness, we believe everyone deserves access to world-class training. 
                Whether you're a beginner or an athlete, our programs are designed to push your limits 
                and help you become the <span className="text-[#FF3B30] font-semibold">Greatest Of All Time</span>.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+919888156428">
                  <Button 
                    className="bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider rounded-none"
                  >
                    Book Free Consultation
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-['Inter'] uppercase text-xs tracking-[0.2em] text-[#FF3B30] mb-4">
                Why G.O.A.T Fitness?
              </p>
              <h2 className="font-['Barlow_Condensed'] font-extrabold text-4xl sm:text-5xl uppercase tracking-tighter text-white mb-6">
                Pathankot's Premier Fitness Destination
              </h2>
              <p className="text-zinc-400 mb-8">
                We're not just a gym – we're a community dedicated to helping you become the best version of yourself. 
                Here's what sets us apart from the rest.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />
                    <span className="text-zinc-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div 
                className="aspect-square bg-cover bg-center"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80)',
                  filter: 'grayscale(50%)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              
              {/* Stats Overlay */}
              <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-4">
                <div className="bg-[#050505]/90 border border-zinc-800 p-4 text-center">
                  <p className="font-['Barlow_Condensed'] font-extrabold text-2xl text-[#FF3B30]">500+</p>
                  <p className="text-zinc-500 text-xs uppercase">Happy Members</p>
                </div>
                <div className="bg-[#050505]/90 border border-zinc-800 p-4 text-center">
                  <p className="font-['Barlow_Condensed'] font-extrabold text-2xl text-[#FF3B30]">5+</p>
                  <p className="text-zinc-500 text-xs uppercase">Years Experience</p>
                </div>
                <div className="bg-[#050505]/90 border border-zinc-800 p-4 text-center">
                  <p className="font-['Barlow_Condensed'] font-extrabold text-2xl text-[#FF3B30]">6AM</p>
                  <p className="text-zinc-500 text-xs uppercase">To 10PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial / CTA Section */}
      <section className="py-24 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient opacity-50" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <Star className="w-12 h-12 text-[#FF3B30] mx-auto mb-6" />
          <h2 className="font-['Barlow_Condensed'] font-extrabold text-3xl sm:text-4xl uppercase tracking-tighter text-white mb-6">
            Ready to Become the G.O.A.T?
          </h2>
          <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
            Join Pathankot's finest gym today and start your transformation journey with 
            IIFEM certified trainers who care about your success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919888156428">
              <Button 
                className="bg-[#FF3B30] hover:bg-white hover:text-black text-white font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-none shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)]"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call: 098881 56428
              </Button>
            </a>
            <a 
              href="https://wa.me/919888156428?text=Hi%2C%20I'm%20interested%20in%20joining%20G.O.A.T%20Fitness"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                variant="outline"
                className="border-white/20 text-white hover:border-[#FF3B30] hover:text-[#FF3B30] font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-none"
              >
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <p className="font-['Inter'] uppercase text-xs tracking-[0.2em] text-[#FF3B30] mb-4">
                Visit Us Today
              </p>
              <h2 className="font-['Barlow_Condensed'] font-extrabold text-4xl sm:text-5xl uppercase tracking-tighter text-white mb-6">
                Get In Touch
              </h2>
              <p className="font-['Inter'] text-zinc-400 mb-8">
                Come experience the best fitness facility in Pathankot. 
                Our team is ready to help you start your transformation journey.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#FF3B30]" />
                  </div>
                  <div>
                    <h4 className="font-['Barlow_Condensed'] font-bold text-lg uppercase text-white mb-1">Location</h4>
                    <p className="text-zinc-400">
                      G.O.A.T Fitness<br />
                      Above HDFC Bank, Saili Road<br />
                      Pathankot, Punjab 145001
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#FF3B30]" />
                  </div>
                  <div>
                    <h4 className="font-['Barlow_Condensed'] font-bold text-lg uppercase text-white mb-1">Phone</h4>
                    <a href="tel:+919888156428" className="text-zinc-400 hover:text-[#FF3B30] transition-colors">
                      098881 56428
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#FF3B30]" />
                  </div>
                  <div>
                    <h4 className="font-['Barlow_Condensed'] font-bold text-lg uppercase text-white mb-1">Timings</h4>
                    <p className="text-zinc-400">
                      Monday - Saturday: 6:00 AM - 10:00 PM<br />
                      Sunday: By Appointment
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Google Maps Embed Placeholder */}
            <div className="relative h-80 lg:h-auto min-h-[400px]">
              <div className="absolute inset-0 bg-zinc-900 border border-zinc-800">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3393.8!2d75.6421!3d32.2643!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDE1JzUxLjUiTiA3NcKwMzgnMzEuNiJF!5e0!3m2!1sen!2sin!4v1600000000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(80%) invert(90%)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="G.O.A.T Fitness Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#050505] border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src={LOGO_URL} 
                alt="G.O.A.T Fitness" 
                className="h-10 w-auto"
              />
            </div>
            <div className="text-center">
              <p className="text-zinc-500 text-sm">
                © 2024 G.O.A.T Fitness, Pathankot. Best Gym in Pathankot.
              </p>
              <p className="text-zinc-600 text-xs mt-1">
                Designed & Developed by{' '}
                <a 
                  href="https://www.wizbang.in" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#FF3B30] hover:text-white transition-colors"
                >
                  Wizbang India
                </a>
              </p>
            </div>
            <Link 
              to="/login" 
              className="text-zinc-700 hover:text-zinc-500 text-xs transition-colors"
              title="Admin"
            >
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
